import { Search } from "lucide-react";

import type { Fax } from "@/app/(authenticated)/fax/_modules/types/types";
import { Input } from "@/app/_module/components/ui/input";
import type {
  FaxDirectionFilter,
  FaxStatusFilter,
} from "@/app/_module/types/fax";

interface FaxFiltersProps {
  searchQuery: string;
  onSearchChange: (query: string) => void;
  directionFilter: FaxDirectionFilter;
  onDirectionChange: (direction: FaxDirectionFilter) => void;
  statusFilter: FaxStatusFilter;
  onStatusChange: (status: FaxStatusFilter) => void;
  allFaxes?: Fax[];
  hideDirectionFilter?: boolean;
}

export const FaxFilters = function({
  searchQuery,
  onSearchChange,
  directionFilter,
  onDirectionChange,
  statusFilter,
  onStatusChange,
  allFaxes = [],
  hideDirectionFilter = false,
}: FaxFiltersProps) {
  // Status = the only two real fax OUTCOMES. Transient states (sending/pending/
  // processing) settle in seconds and never linger in history, so there's no
  // point filtering them. Delivered = sent/success/delivered/received (a fax
  // that transmits IS delivered — no read receipts); Failed = failed & friends.
  const statuses: FaxStatusFilter[] = ["all", "delivered", "failed"];
  const statusLabel: Record<string, string> = {
    all: "All",
    delivered: "Delivered",
    failed: "Failed",
  };
  const DELIVERED_SET = new Set(["sent", "success", "delivered", "received"]);
  const FAILED_SET = new Set(["failed", "busy", "no-answer", "no_answer", "invalid-number", "invalid_number", "cancelled"]);

  // Counts: statuses for the Status axis, in/outbound for the Type axis.
  const counts: Record<string, number> = { delivered: 0, failed: 0, inbound: 0, outbound: 0 };
  for (const fax of allFaxes) {
    const st = String(fax.status);
    if (DELIVERED_SET.has(st)) counts.delivered += 1;
    else if (FAILED_SET.has(st)) counts.failed += 1;
    if (fax.direction === "inbound") counts.inbound += 1;
    else if (fax.direction === "outbound") counts.outbound += 1;
  }

  const segBtn = (active: boolean) =>
    // Matches the settings tab look: active = white pill with a 2px black
    // border + shadow. Inactive keeps a transparent 2px border so nothing shifts.
    //
    // A global rule in globals.css (`button:focus { border-color: inherit
    // !important; box-shadow: none !important }`) strips the border + shadow
    // while the button is focused — which is why the black line only appeared
    // after you clicked away (blur). The `focus:!border-black`/`focus:!shadow-md`
    // variants compile to `.class:focus` selectors that out-specify `button:focus`
    // and carry !important, so the active pill keeps its border while focused.
    `px-3 py-1.5 text-sm rounded-md border-2 transition-colors whitespace-nowrap ${
      active
        ? "bg-background border-black focus:!border-black shadow-md focus:!shadow-md font-medium text-foreground"
        : "border-transparent text-muted-foreground hover:text-foreground"
    }`;

  const directions: { key: FaxDirectionFilter; label: string; count: number }[] = [
    { key: "all", label: "All", count: allFaxes.length },
    { key: "outbound", label: "Sent", count: counts.outbound },
    { key: "inbound", label: "Received", count: counts.inbound },
  ];

  return (
    <div className="space-y-4">
      {/* Search bar */}
      <div className="relative max-w-md">
        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground h-4 w-4" />
        <Input
          className="pl-10 bg-muted/50"
          onChange={(e) => onSearchChange(e.target.value)}
          placeholder="Search fax number, name, or filename..."
          value={searchQuery}
        />
      </div>

      {/* Two clear, non-overlapping filters: Type (direction) and Status */}
      <div className="flex flex-wrap items-center gap-x-6 gap-y-3">
        {!hideDirectionFilter && (
          <div className="flex items-center gap-2">
            <span className="text-xs font-medium text-muted-foreground">Type</span>
            <div className="inline-flex items-center rounded-lg bg-muted p-1">
              {directions.map((d) => (
                <button
                  key={d.key}
                  type="button"
                  onClick={() => onDirectionChange(d.key)}
                  className={segBtn(directionFilter === d.key)}
                >
                  {d.label} <span className="opacity-60 text-xs">{d.count}</span>
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Status is meaningless for Received (inbound faxes only ever "arrive"),
            so hide it when viewing Received to avoid a permanently-empty Failed. */}
        {directionFilter !== "inbound" && (
        <div className="flex items-center gap-2">
          <span className="text-xs font-medium text-muted-foreground">Status</span>
          <div className="inline-flex items-center rounded-lg bg-muted p-1">
            {statuses.map((status) => {
              const count = status === "all" ? allFaxes.length : counts[status] ?? 0;
              return (
                <button
                  key={status}
                  type="button"
                  onClick={() => onStatusChange(status)}
                  className={segBtn(statusFilter === status)}
                >
                  {statusLabel[status] ?? status}{" "}
                  <span className="opacity-60 text-xs">{count}</span>
                </button>
              );
            })}
          </div>
        </div>
        )}
      </div>
    </div>
  );
}
